/**
 * CallHistoryOut -> what the call list renders.
 *
 * Two reshaping decisions, both permitted (rename, sort) and both deliberate:
 *
 *   1. Calls are ordered NEWEST FIRST, which is also the order the API returns
 *      them in. The panel read oldest-first until 3 September 2026 on the
 *      argument that a conversation is read forwards; in use it meant opening a
 *      deal and landing on a call from months ago, with the one that changed
 *      the recommendation somewhere below the fold. Freshest information first
 *      (Mike, 3 September 2026). The sort is still explicit rather than a
 *      reliance on the API's order — the old frontend trusted that order and
 *      resolved "latest" to the OLDEST call for a while.
 *
 *      `callNumber` keeps its chronological meaning: call 1 is the first
 *      conversation whatever row it is drawn on.
 *
 *   2. Signal badges arrive as one list carrying both confirmed and unconfirmed,
 *      and mixing the seven qualification signals with the five progression
 *      ones. They are split three ways for display — confirmed evidence, missing
 *      evidence, and commercial progress — mirroring `dealCard.ts`, so a call
 *      entry and the deal panel above it count out of the same denominator.
 *      Nothing is added; filtering is by the canonical vocabularies, so ordering
 *      is stable and an unknown key simply does not appear.
 *
 * `recommendation_at_analysis_time` is deliberately NOT surfaced. The backend
 * records it only on calls analysed after the engine began storing a canonical
 * value; earlier rows hold free-text extractor prose, so most entries had no
 * verdict to show. Deriving one from the deal's current recommendation is what
 * the old frontend did (audit D4/V3) and is exactly wrong — that value is
 * computed from every call, not from this one. Rather than render "not
 * recorded" forever, the concept was removed from the UI: history shows what
 * each call established and what the user decided, and the verdict is stated
 * once, currently, at the top of the panel.
 */
import type { CallHistoryItem, SignalBadge, SignalKey } from '../types'
import { PROGRESSION_SIGNALS, QUALIFICATION_SIGNALS } from '../../features/pipeline/labels'

export interface CallEntry {
  id: string
  callNumber: number
  callDate: string | null
  summary: string | null
  /** Qualification evidence this call established, canonical order. */
  confirmedSignals: SignalKey[]
  /** Qualification evidence it did not, canonical order. */
  missingSignals: SignalKey[]
  /** Commercial-progression steps reached on this call. Workflow, not evidence. */
  confirmedProgression: SignalKey[]
  confirmedRisks: string[]
  blockerCount: number
  decision: { value: string; label: string; reason: string | null } | null
}

/** One badge list -> the three groups the panel renders, in canonical order. */
function splitSignals(badges: readonly SignalBadge[]) {
  // `key` is typed loosely on the wire; compare as strings and let the canonical
  // vocabularies decide what is real, so an unknown key is dropped rather than
  // rendered raw.
  const confirmed = new Set(badges.filter((b) => b.confirmed).map((b) => String(b.key)))
  const present = new Set(badges.map((b) => String(b.key)))
  return {
    confirmedSignals: QUALIFICATION_SIGNALS.filter((k) => confirmed.has(String(k))),
    missingSignals: QUALIFICATION_SIGNALS.filter((k) => present.has(String(k)) && !confirmed.has(String(k))),
    confirmedProgression: PROGRESSION_SIGNALS.filter((k) => confirmed.has(String(k))),
  }
}

export function toCallEntries(items: readonly CallHistoryItem[]): CallEntry[] {
  return [...items]
    .sort((a, b) => b.call_number - a.call_number)
    .map((item) => ({
      id: item.call_snapshot_id,
      callNumber: item.call_number,
      callDate: item.call_date ?? null,
      summary: item.call_summary ?? null,
      ...splitSignals(item.confirmed_signal_badges),
      confirmedRisks: item.risks.filter((r) => r.confirmed).map((r) => r.label),
      blockerCount: Array.isArray(item.blockers) ? item.blockers.length : 0,
      decision: item.call_decision
        ? {
            value: item.call_decision.decision,
            label: item.call_decision.label,
            reason: item.call_decision.reason ?? null,
          }
        : null,
    }))
}
